import { createAsyncThunk } from '@reduxjs/toolkit';
import { tr } from '../../i18n/i18n';
import i18n from '../../i18n/i18n';
import axios, { AxiosError } from 'axios';
import { ICreateSubjectError } from '../../interface/subjectModel';
import {
  FeeTemplateByIdResponse,
  IFeeTemplateDetails,
  IFeeTemplatesResponse,
  IFetchFrequency,
  IGetAssignClassroom,
  IGetFeeTemplateRequestParams,
  IFeeTemplateStudentsResponse,
} from '../../interface/feeTemplateModel';
import {
  fetchFeeTemplatesService,
  fetchFeeTemplateByIdService,
  fetchClassroomAssignedToFeeTemplateService,
  updateClassroomAssignedToFeeTemplateService,
  fetchFrequencyService,
  createFeeTemplateService,
  updateFeeTemplateService,
  deleteFeeTemplateService,
  fetchStudentsByFeeTemplateService,
} from '../../services/feeTemplate';

export const fetchFeeTemplates = createAsyncThunk<
  IFeeTemplatesResponse,
  IGetFeeTemplateRequestParams,
  { rejectValue: ICreateSubjectError }
>('payment/getFeeTemplate', async (params, { rejectWithValue }) => {
  try {
    const response = await fetchFeeTemplatesService(params);
    return response;
  } catch (err: any) {
    if (axios.isAxiosError(err)) {
      const error = err as AxiosError<ICreateSubjectError>;
      return rejectWithValue({
        status: error.response?.status || 500,
        message: error.response?.data?.message || i18n.t('toast.unknown_error', 'Unknown error'),
      });
    } else {
      return rejectWithValue({
        status: err.status || 500,
        message: err.message,
      });
    }
  }
});

export const fetchFeeTemplatesById = createAsyncThunk<
  FeeTemplateByIdResponse,
  string,
  { rejectValue: ICreateSubjectError }
>('payment/getFeeTemplateById', async (id, { rejectWithValue }) => {
  try {
    const response = await fetchFeeTemplateByIdService(id);
    return response;
  } catch (err: any) {
    if (axios.isAxiosError(err)) {
      const error = err as AxiosError<ICreateSubjectError>;
      return rejectWithValue({
        status: error.response?.status || 500,
        message: error.response?.data?.message || i18n.t('toast.unknown_error', 'Unknown error'),
      });
    } else {
      return rejectWithValue({
        status: err.status || 500,
        message: err.message,
      });
    }
  }
});

export const fetchClassroomAssignedToFeeTemplate = createAsyncThunk<
  IGetAssignClassroom,
  string,
  { rejectValue: ICreateSubjectError }
>('payment/getFeeTemplateClassroom', async (id, { rejectWithValue }) => {
  try {
    const response = await fetchClassroomAssignedToFeeTemplateService(id);
    return response;
  } catch (err: any) {
    if (axios.isAxiosError(err)) {
      const error = err as AxiosError<ICreateSubjectError>;
      return rejectWithValue({
        status: error.response?.status || 500,
        message: error.response?.data?.message || i18n.t('toast.unknown_error', 'Unknown error'),
      });
    } else {
      return rejectWithValue({
        status: err.status || 500,
        message: err.message,
      });
    }
  }
});

export const updateClassroomAssignedToFeeTemplate = createAsyncThunk<
  IGetAssignClassroom,
  { body: string[]; id: string },
  { rejectValue: ICreateSubjectError }
>('payment/updateFeeTemplateClassroom', async ({ id, body }, { rejectWithValue }) => {
  try {
    const response = await updateClassroomAssignedToFeeTemplateService(id, body);
    return response;
  } catch (err: any) {
    if (axios.isAxiosError(err)) {
      const error = err as AxiosError<ICreateSubjectError>;
      return rejectWithValue({
        status: error.response?.status || 500,
        message: error.response?.data?.message || i18n.t('toast.unknown_error', 'Unknown error'),
      });
    } else {
      return rejectWithValue({
        status: err.status || 500,
        message: err.message,
      });
    }
  }
});

export const fetchFrequency = createAsyncThunk<
  IFetchFrequency,
  void,
  { rejectValue: ICreateSubjectError }
>('payment/getFrequency', async (_, { rejectWithValue }) => {
  try {
    const response = await fetchFrequencyService();
    return response;
  } catch (err: any) {
    if (axios.isAxiosError(err)) {
      const error = err as AxiosError<ICreateSubjectError>;
      return rejectWithValue({
        status: error.response?.status || 500,
        message: error.response?.data?.message || i18n.t('toast.unknown_error', 'Unknown error'),
      });
    } else {
      return rejectWithValue({
        status: err.status || 500,
        message: err.message,
      });
    }
  }
});

export const createFeeTemplate = createAsyncThunk<
  IFetchFrequency,
  IFeeTemplateDetails,
  { rejectValue: ICreateSubjectError }
>('payment/createFeeTemplate', async (body, { rejectWithValue }) => {
  try {
    const response = await createFeeTemplateService(body);
    return response;
  } catch (err: any) {
    if (axios.isAxiosError(err)) {
      const error = err as AxiosError<ICreateSubjectError>;
      return rejectWithValue({
        status: error.response?.status || 500,
        message: error.response?.data?.message || i18n.t('toast.unknown_error', 'Unknown error'),
      });
    } else {
      return rejectWithValue({
        status: err.status || 500,
        message: err.message,
      });
    }
  }
});

export const updateFeeTemplate = createAsyncThunk<
  IFetchFrequency,
  { body: IFeeTemplateDetails; id: string },
  { rejectValue: ICreateSubjectError }
>('payment/updateFeeTemplate', async ({ body, id }, { rejectWithValue }) => {
  try {
    const response = await updateFeeTemplateService(id, body);
    return response;
  } catch (err: any) {
    if (axios.isAxiosError(err)) {
      const error = err as AxiosError<ICreateSubjectError>;
      return rejectWithValue({
        status: error.response?.status || 500,
        message: error.response?.data?.message || i18n.t('toast.unknown_error', 'Unknown error'),
      });
    } else {
      return rejectWithValue({
        status: err.status || 500,
        message: err.message,
      });
    }
  }
});

export const deleteFeeTemplate = createAsyncThunk<
  IFetchFrequency,
  string,
  { rejectValue: ICreateSubjectError }
>('payment/deleteFeeTemplate', async (id, { rejectWithValue }) => {
  try {
    const response = await deleteFeeTemplateService(id);
    return response;
  } catch (err: any) {
    if (axios.isAxiosError(err)) {
      const error = err as AxiosError<ICreateSubjectError>;
      return rejectWithValue({
        status: error.response?.status || 500,
        message: error.response?.data?.message || i18n.t('toast.unknown_error', 'Unknown error'),
      });
    } else {
      return rejectWithValue({
        status: err.status || 500,
        message: err.message,
      });
    }
  }
});

export const fetchStudentsByFeeTemplate = createAsyncThunk<
  IFeeTemplateStudentsResponse,
  string,
  { rejectValue: ICreateSubjectError }
>('payment/fetchStudentsByFeeTemplate', async (feeTemplateId, { rejectWithValue }) => {
  try {
    const response = await fetchStudentsByFeeTemplateService(feeTemplateId);
    return response;
  } catch (err: any) {
    if (axios.isAxiosError(err)) {
      const error = err as AxiosError<ICreateSubjectError>;
      return rejectWithValue({
        status: error.response?.status || 500,
        message: error.response?.data?.message || i18n.t('toast.unknown_error', 'Unknown error'),
      });
    } else {
      return rejectWithValue({
        status: err.status || 500,
        message: err.message,
      });
    }
  }
});
